import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
// All icons now imported from BespokeIcons for premium visual identity
import { toast, Toaster } from 'sonner';
import Credits from './Credits';
import { GRAY_WORDS_DICTIONARY } from './GrayWordsAnalyzer';
import AdminFeedbackButton from './AdminFeedbackButton';
import EmpathySOS from './EmpathySOS';
import { 
  ObserveIcon, FeelIcon, NeedIcon, GoldIcon,
  HomeIcon, ProfileIcon, FlowIcon, WisdomIcon,
  ShieldIcon, ClockIcon, UsersIcon, PlayIcon,
  SparkleIcon, CompassIcon, ArrowIcon, RefreshIcon,
  LoveAlchemyIcon, CircleOfSoulsIcon, SanctuaryIcon, EternalMemoryIcon,
  BookIcon, CalendarIcon, ChevronIcon, HeartIcon, MairyIcon
} from './BespokeIcons';

// Golden Ratio for mathematical beauty
const PHI = 1.618033988749895;
const FIBONACCI = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];

// ============================================
// ANIMATED LOGO COMPONENT - Multicolor ElchmE
// ============================================
const AnimatedLogo = ({ size = 'lg', showBeta = false }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  const letters = [
    { text: 'E', color: '#F59E0B', delay: 0 },
    { text: 'l', color: '#3B82F6', delay: 0.08 },
    { text: 'c', color: '#EF4444', delay: 0.16 },
    { text: 'h', color: '#EF4444', delay: 0.24 },
    { text: 'm', color: '#10B981', delay: 0.32 },
    { text: 'E', color: '#F59E0B', delay: 0.4 },
  ];

  const sizeClasses = {
    sm: 'text-3xl sm:text-4xl',
    md: 'text-4xl sm:text-5xl',
    lg: 'text-5xl sm:text-6xl lg:text-7xl',
    xl: 'text-6xl sm:text-7xl lg:text-8xl'
  };

  return (
    <div 
      className="flex items-end gap-0.5 select-none relative" 
      data-testid="brand-logo"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {letters.map((letter, i) => (
        <span
          key={i}
          data-testid="brand-logo-letter"
          className={`${sizeClasses[size]} font-semibold transition-all duration-700`}
          style={{
            color: letter.color,
            textShadow: `0 0 40px ${letter.color}60, 0 0 80px ${letter.color}30`,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: `${letter.delay}s`,
            animation: mounted ? `breathe 4s ease-in-out infinite ${letter.delay + 1}s` : 'none'
          }}
        >
          {letter.text}
        </span>
      ))}
      {showBeta && (
        <span 
          className="absolute -top-1 -right-8 text-[10px] font-medium tracking-wider text-amber-400/70 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20"
          style={{ fontFamily: 'Figtree, sans-serif' }}
        >
          beta
        </span>
      )}
    </div>
  );
};

// ============================================
// GOLDEN PARTICLES CANVAS
// ============================================
const GoldenParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const DPR = window.devicePixelRatio || 1;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.scale(DPR, DPR);
    };
    resize();
    window.addEventListener('resize', resize);

    // Create particles
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.clientWidth,
      y: Math.random() * canvas.clientHeight,
      r: Math.random() * 1.5 + 0.5,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      o: Math.random() * 0.5 + 0.2,
      pulse: Math.random() * Math.PI * 2
    }));

    let animationId;
    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      for (const p of particles) {
        // Update position
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.02;

        // Wrap around
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        // Pulsing opacity
        const opacity = p.o + Math.sin(p.pulse) * 0.15;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(245, 158, 11, ${opacity})`;
        ctx.fill();

        // Add glow effect for some particles
        if (p.r > 1) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(245, 158, 11, ${opacity * 0.1})`;
          ctx.fill();
        }
      }

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="gold-particles"
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none w-full h-full"
      style={{ opacity: 0.6 }}
    />
  );
};

// ============================================
// SLOW BACKGROUND SPIRAL - Barely visible lava lamp
// ============================================
const SlowBackgroundSpiral = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const DPR = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = window.innerWidth * DPR;
      canvas.height = window.innerHeight * DPR;
      ctx.scale(DPR, DPR);
    };
    resize();
    window.addEventListener('resize', resize);

    const blobs = [];
    const numBlobs = 12; // Fewer blobs for subtlety
    
    for (let i = 0; i < numBlobs; i++) {
      blobs.push({
        baseAngle: (i * 2 * Math.PI) / numBlobs,
        baseRadius: 100 + Math.random() * 200,
        baseSize: 80 + Math.random() * 150,
        zPhase: Math.random() * Math.PI * 2,
        // EXTREMELY SLOW speeds (0.02 - 0.05x of normal)
        zSpeed: 0.01 + Math.random() * 0.02,
        zAmplitude: 150 + Math.random() * 200,
        hue: (40 + Math.random() * 20) % 360, // Golden/amber hues
        saturation: 40 + Math.random() * 20,
        orbitSpeed: 0.005 + Math.random() * 0.01,
        pulseSpeed: 0.008 + Math.random() * 0.01,
        wobbleSpeed: 0.003 + Math.random() * 0.005,
        wobbleAmount: 30 + Math.random() * 50,
      });
    }

    let animationTime = 0;
    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      
      // Very slow fade for dreamy effect
      ctx.fillStyle = 'rgba(8, 10, 14, 0.02)';
      ctx.fillRect(0, 0, w, h);

      animationTime += 1; // Very slow increment

      const centerX = w / 2;
      const centerY = h / 2;

      blobs.forEach((blob) => {
        const theta = blob.baseAngle + animationTime * blob.orbitSpeed;
        const zDepth = Math.sin(animationTime * blob.zSpeed + blob.zPhase) * blob.zAmplitude;
        const perspective = 400 / (400 + zDepth);
        
        const wobbleX = Math.sin(animationTime * blob.wobbleSpeed) * blob.wobbleAmount;
        const wobbleY = Math.cos(animationTime * blob.wobbleSpeed * 0.7) * blob.wobbleAmount;
        
        const radius = blob.baseRadius * perspective;
        const x = centerX + Math.cos(theta) * radius + wobbleX;
        const y = centerY + Math.sin(theta) * radius * 0.6 + wobbleY;
        
        const pulseSize = Math.sin(animationTime * blob.pulseSpeed) * 30;
        const blobSize = (blob.baseSize + pulseSize) * perspective;
        
        const hue = (blob.hue + animationTime * 0.005) % 360;
        const lightness = 30 + zDepth * 0.05;
        // Very low alpha for barely visible effect
        const alpha = (0.03 + perspective * 0.03);

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, blobSize);
        gradient.addColorStop(0, `hsla(${hue}, ${blob.saturation}%, ${lightness + 20}%, ${alpha})`);
        gradient.addColorStop(0.4, `hsla(${hue}, ${blob.saturation}%, ${lightness}%, ${alpha * 0.6})`);
        gradient.addColorStop(0.7, `hsla(${hue}, ${blob.saturation - 10}%, ${lightness - 10}%, ${alpha * 0.3})`);
        gradient.addColorStop(1, `hsla(${hue}, ${blob.saturation - 20}%, ${lightness - 20}%, 0)`);

        ctx.beginPath();
        ctx.arc(x, y, blobSize, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      });

      // Subtle central glow
      const coreSize = 100 + Math.sin(animationTime * 0.003) * 20;
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
      coreGradient.addColorStop(0, 'rgba(245, 158, 11, 0.05)');
      coreGradient.addColorStop(0.5, 'rgba(245, 158, 11, 0.02)');
      coreGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
      ctx.fill();

      animationRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.4 }}
      aria-hidden="true"
    />
  );
};

// ============================================
// BREATHING CIRCLE
// ============================================
const BreathingCircle = ({ onClick, size = 'md' }) => {
  const sizes = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32'
  };

  return (
    <button
      onClick={onClick}
      className={`relative ${sizes[size]} group cursor-pointer`}
      aria-label="Begin meditation"
      data-testid="breathing-circle"
    >
      <div 
        className="absolute inset-0 rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 backdrop-blur-sm"
        style={{ animation: 'breathe-scale 4s ease-in-out infinite' }}
      />
      <div className="absolute inset-[18%] rounded-full bg-amber-400/20 blur-md" />
      <div className="absolute inset-0 flex items-center justify-center">
        <PlayIcon size={24} color="#F59E0B" className="group-hover:scale-110 transition-transform" />
      </div>
    </button>
  );
};

// ============================================
// OFNR FLOW INDICATOR - With whimsical sounds and haptic feedback
// ============================================

// Whimsical chime frequencies for each step (pentatonic scale for pleasant sounds)
const CHIME_NOTES = {
  Observe: [523.25, 659.25], // C5, E5 - curious, opening
  Feel: [440, 554.37],       // A4, C#5 - warm, heart-centered
  Need: [392, 493.88],       // G4, B4 - grounding, growth
  Gold: [587.33, 783.99, 1046.5], // D5, G5, C6 - triumphant, magical
};

// Create and play a whimsical chime sound
const playChime = (notes, stepLabel) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    notes.forEach((freq, i) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Use different waveforms for different feelings
      if (stepLabel === 'Gold') {
        oscillator.type = 'sine'; // Pure, magical
      } else if (stepLabel === 'Feel') {
        oscillator.type = 'triangle'; // Warm, soft
      } else {
        oscillator.type = 'sine';
      }
      
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      
      // Envelope for gentle attack and decay
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.8 + (i * 0.15));
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start(audioContext.currentTime + (i * 0.08));
      oscillator.stop(audioContext.currentTime + 1 + (i * 0.15));
    });
    
    // Add a soft sparkle for Gold
    if (stepLabel === 'Gold') {
      setTimeout(() => {
        const sparkle = audioContext.createOscillator();
        const sparkleGain = audioContext.createGain();
        sparkle.type = 'sine';
        sparkle.frequency.setValueAtTime(1318.51, audioContext.currentTime); // E6
        sparkleGain.gain.setValueAtTime(0, audioContext.currentTime);
        sparkleGain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
        sparkleGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);
        sparkle.connect(sparkleGain);
        sparkleGain.connect(audioContext.destination);
        sparkle.start();
        sparkle.stop(audioContext.currentTime + 0.5);
      }, 200);
    }
  } catch (e) {
    // Audio not supported, fail silently
    console.log('Audio not available');
  }
};

// Trigger haptic vibration if available
const triggerHaptic = (pattern) => {
  try {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  } catch (e) {
    // Vibration not supported
  }
};

const OFNRFlow = () => {
  const [activeStep, setActiveStep] = useState(null);
  const [ripples, setRipples] = useState({});
  
  // Using bespoke ancient-futuristic icons
  const steps = [
    { icon: ObserveIcon, color: '#3B82F6', label: 'Observe', bg: 'bg-blue-500/10', border: 'border-blue-500/30', vibrate: [30], isBespoke: true },
    { icon: FeelIcon, color: '#EF4444', label: 'Feel', bg: 'bg-red-500/10', border: 'border-red-500/30', vibrate: [20, 30, 20], isBespoke: true },
    { icon: NeedIcon, color: '#10B981', label: 'Need', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', vibrate: [40, 20], isBespoke: true },
    { icon: GoldIcon, color: '#F59E0B', label: 'Gold', bg: 'bg-amber-500/10', border: 'border-amber-500/30', vibrate: [15, 30, 15, 30, 50], isBespoke: true },
  ];

  const handleStepClick = (step, idx) => {
    // Play the chime
    playChime(CHIME_NOTES[step.label], step.label);
    
    // Trigger haptic feedback
    triggerHaptic(step.vibrate);
    
    // Set active for animation
    setActiveStep(idx);
    
    // Create ripple effect
    setRipples(prev => ({ ...prev, [idx]: Date.now() }));
    
    // Clear active after animation
    setTimeout(() => setActiveStep(null), 600);
  };

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="ofnr-flow">
      {steps.map((step, idx) => (
        <React.Fragment key={step.label}>
          {idx > 0 && <ArrowIcon size={12} color="rgba(255,255,255,0.2)" direction="right" className="hidden sm:block" />}
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={() => handleStepClick(step, idx)}
              className={`
                relative w-10 h-10 sm:w-12 sm:h-12 rounded-full ${step.bg} border ${step.border} 
                flex items-center justify-center cursor-pointer
                transition-all duration-300 ease-out
                hover:scale-110 hover:shadow-lg
                active:scale-95
                ${activeStep === idx ? 'animate-wiggle scale-110' : ''}
              `}
              style={{
                boxShadow: activeStep === idx ? `0 0 20px ${step.color}40, 0 0 40px ${step.color}20` : 'none',
              }}
              aria-label={`${step.label} step - click for sound`}
              data-testid={`ofnr-step-${step.label.toLowerCase()}`}
            >
              {/* Ripple effect */}
              {ripples[idx] && (
                <span
                  key={ripples[idx]}
                  className="absolute inset-0 rounded-full animate-ping-once"
                  style={{ 
                    backgroundColor: step.color,
                    opacity: 0.3,
                  }}
                />
              )}
              
              {/* Sparkle particles for Gold */}
              {activeStep === idx && step.label === 'Gold' && (
                <>
                  {[...Array(6)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute w-1 h-1 rounded-full bg-amber-300 animate-sparkle"
                      style={{
                        left: '50%',
                        top: '50%',
                        animationDelay: `${i * 0.1}s`,
                        transform: `rotate(${i * 60}deg) translateY(-20px)`,
                      }}
                    />
                  ))}
                </>
              )}
              
              {/* Render bespoke or standard icon */}
              {step.isBespoke ? (
                <step.icon 
                  size={20}
                  color={step.color}
                  className={`
                    transition-transform duration-300
                    ${activeStep === idx ? 'scale-125' : ''}
                  `}
                />
              ) : (
                <step.icon 
                  className={`
                    w-5 h-5 transition-transform duration-300
                    ${activeStep === idx ? 'scale-125' : ''}
                  `} 
                  style={{ color: step.color }} 
                />
              )}
            </button>
            <span 
              className={`
                text-[10px] sm:text-xs font-medium transition-all duration-300
                ${activeStep === idx ? 'scale-110 font-bold' : ''}
              `} 
              style={{ color: step.color }}
            >
              {step.label}
            </span>
          </div>
        </React.Fragment>
      ))}
      
      {/* Custom animations */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg) scale(1.1); }
          15% { transform: rotate(-8deg) scale(1.15); }
          30% { transform: rotate(8deg) scale(1.1); }
          45% { transform: rotate(-5deg) scale(1.12); }
          60% { transform: rotate(5deg) scale(1.1); }
          75% { transform: rotate(-2deg) scale(1.1); }
        }
        .animate-wiggle {
          animation: wiggle 0.6s ease-in-out;
        }
        
        @keyframes ping-once {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .animate-ping-once {
          animation: ping-once 0.6s ease-out forwards;
        }
        
        @keyframes sparkle {
          0% { opacity: 1; transform: rotate(var(--rotation, 0deg)) translateY(-5px) scale(0); }
          50% { opacity: 1; transform: rotate(var(--rotation, 0deg)) translateY(-25px) scale(1.5); }
          100% { opacity: 0; transform: rotate(var(--rotation, 0deg)) translateY(-35px) scale(0); }
        }
        .animate-sparkle {
          animation: sparkle 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// ============================================
// FEATURE CARDS - With Bespoke Icons
// ============================================
const FeatureCard = ({ icon: Icon, title, description, color, isBespoke = false, iconColor }) => (
  <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all group" data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5 sm:p-6">
      <div className={`w-12 h-12 rounded-lg bg-${color}-500/10 border border-${color}-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        {isBespoke ? (
          <Icon size={28} color={iconColor} />
        ) : (
          <Icon className={`w-5 h-5 text-${color}-400`} />
        )}
      </div>
      <h3 className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <p className="text-white/60 text-sm leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);

// ============================================
// BOTTOM NAVIGATION
// ============================================
export const BottomNav = ({ active = 'home' }) => {
  const navigate = useNavigate();
  
  // Using bespoke icons for premium navigation
  const items = [
    { id: 'home', icon: HomeIcon, label: 'Home', path: '/', color: '#F59E0B', isBespoke: true },
    { id: 'flow', icon: FlowIcon, label: 'Flow', path: '/elchme/flow', color: '#06B6D4', isBespoke: true },
    { id: 'sketch', icon: SparkleIcon, label: 'Sketch', path: '/sketch', color: '#10B981', isBespoke: true },
    { id: 'profile', icon: ProfileIcon, label: 'Profile', path: '/dashboard', color: '#8B5CF6', isBespoke: true },
  ];

  return (
    <nav 
      className="fixed bottom-0 inset-x-0 h-16 backdrop-blur-md bg-black/80 border-t border-white/10 z-50 safe-area-pb" 
      role="navigation" 
      data-testid="bottom-nav"
    >
      <ul className="grid grid-cols-4 h-full">
        {items.map((item) => (
          <li key={item.id}>
            {item.isExternal ? (
              <a 
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`bottom-nav-${item.id}`}
                className={`w-full h-full flex flex-col items-center justify-center gap-0.5 transition-all ${
                  active === item.id ? 'scale-110' : 'opacity-60 hover:opacity-100'
                }`}
              >
                {item.isBespoke ? (
                  <item.icon size={22} color={active === item.id ? item.color : '#9CA3AF'} />
                ) : (
                  <item.icon className="w-5 h-5" style={{ color: active === item.id ? item.color : '#9CA3AF' }} />
                )}
                <span 
                  className="text-[10px] font-medium"
                  style={{ color: active === item.id ? item.color : '#9CA3AF' }}
                >
                  {item.label}
                </span>
              </a>
            ) : (
              <button 
                onClick={() => navigate(item.path)}
                data-testid={`bottom-nav-${item.id}`}
                className={`w-full h-full flex flex-col items-center justify-center gap-0.5 transition-all ${
                  active === item.id ? 'scale-110' : 'opacity-60 hover:opacity-100'
                }`}
              >
                {item.isBespoke ? (
                  <item.icon size={22} color={active === item.id ? item.color : '#9CA3AF'} />
                ) : (
                  <item.icon className="w-5 h-5" style={{ color: active === item.id ? item.color : '#9CA3AF' }} />
                )}
                <span 
                  className="text-[10px] font-medium"
                  style={{ color: active === item.id ? item.color : '#9CA3AF' }}
                >
                  {item.label}
                </span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ============================================
// LOGIN DIALOG
// ============================================
const LoginDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store user data
      localStorage.setItem('elchme_user_id', data.user_id);
      localStorage.setItem('elchme_user_name', data.name);
      localStorage.setItem('elchme_user_email', email);

      toast.success(`Welcome back, ${data.name}!`);
      onOpenChange(false);
      navigate('/elchme');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1419] border-white/10 text-white max-w-sm" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="text-amber-400">Welcome to </span>
            <span className="text-blue-400">El</span>
            <span className="text-red-400">ch</span>
            <span className="text-emerald-400">m</span>
            <span className="text-amber-400">E</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleLogin} className="space-y-4 mt-4">
          <div>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
              data-testid="login-password-input"
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold py-5"
            data-testid="login-submit-btn"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
          <div className="border-t border-white/10 pt-4 mt-4">
            <p className="text-center text-white/50 text-xs mb-3">
              New to the family?
            </p>
            <a 
              href="https://belongingsessions.com/join?redirect=elchme" 
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2 px-4 border border-amber-500/30 rounded-md text-amber-400 hover:bg-amber-500/10 transition-colors text-sm"
            >
              Join at belongingsessions.com
            </a>
            <p className="text-center text-white/30 text-[10px] mt-2">
              One account for ElchmE, Lore, and the whole ecosystem
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// MAIN LANDING COMPONENT
// ============================================
export default function ElchmELanding() {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);

  useEffect(() => {
    // Check for SSO token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('sso');
    
    if (ssoToken) {
      // Validate SSO token and auto-login
      handleSSOLogin(ssoToken);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Check for existing session
    const userId = localStorage.getItem('elchme_user_id');
    const userName = localStorage.getItem('elchme_user_name');
    if (userId && userName) {
      setUser({ id: userId, name: userName });
    }
  }, []);

  const handleSSOLogin = async (token) => {
    setSsoLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sso/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'SSO validation failed');
      }

      // Store user data
      localStorage.setItem('elchme_user_id', data.user_id);
      localStorage.setItem('elchme_user_name', data.name);
      localStorage.setItem('elchme_user_email', data.email);

      setUser({ id: data.user_id, name: data.name });
      toast.success(`Welcome, ${data.name}! Auto-signed in from Belonging Sessions.`);
      
      // Navigate to ElchmE flow
      navigate('/elchme');
    } catch (error) {
      console.error('SSO login failed:', error);
      toast.error('SSO sign-in failed. Please try manual login.');
    } finally {
      setSsoLoading(false);
    }
  };

  const handleBegin = () => {
    navigate('/elchme');
  };

  return (
    <div className="min-h-screen bg-[#080A0E] text-white overflow-x-hidden">
      {/* Slow-moving Grant Spiral Background - barely visible */}
      <SlowBackgroundSpiral />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
        data-testid="landing-hero-section"
      >
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-900/5 to-transparent" />
        <GoldenParticles />
        
        {/* Radial Glow */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 40%, rgba(245, 158, 11, 0.08) 0%, transparent 50%)'
          }}
        />

        {/* Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto py-20">
          {/* SSO Loading Indicator */}
          {ssoLoading && (
            <div className="mb-8 inline-flex items-center gap-3 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400">
              <RefreshIcon size={16} color="#FBBF24" className="animate-spin" />
              <span>Signing you in automatically...</span>
            </div>
          )}

          {/* User Badge */}
          {user && !ssoLoading && (
            <div className="mb-8 inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-white/60">
              <ProfileIcon size={16} color="#9CA3AF" />
              <span>Welcome, <span className="text-amber-400">{user.name}</span></span>
            </div>
          )}

          {/* Animated Logo */}
          <div className="mb-6">
            <AnimatedLogo size="xl" showBeta={true} />
          </div>

          {/* Tagline */}
          <h1 
            className="text-xl sm:text-2xl lg:text-3xl font-light text-amber-500/90 tracking-wide mb-4"
            style={{ fontFamily: "'Figtree', sans-serif" }}
          >
            Empathetic Alchemy
          </h1>

          {/* Subtitle */}
          <p className="text-white/60 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            Transform conflict — and compliments — into gold through compassionate communication.
            ElchmE is a simple, guided practice that helps you move from tension and misunderstanding into clarity, belonging, and actionable solutions.
            Join the Belonging Sessions family and discover the golden thread that connects us all—then learn how to untangle and reveal it, whenever life gets knotted.
          </p>

          {/* OFNR Flow */}
          <div className="mb-10">
            <OFNRFlow />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              onClick={handleBegin}
              className="w-full sm:w-auto px-8 py-6 text-lg font-semibold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black rounded-lg shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-all hover:scale-105"
              data-testid="cta-begin-button"
            >
              <GoldIcon size={20} color="#000000" className="mr-2" />
              Begin the ElchmE
            </Button>

            {user ? (
              <a 
                href="https://belongingsessions.com/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-5 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                data-testid="cta-dashboard-button"
              >
                <CompassIcon size={20} color="#F59E0B" className="mr-2" />
                My Dashboard
              </a>
            ) : (
              <Button 
                onClick={() => setLoginOpen(true)}
                variant="outline"
                className="w-full sm:w-auto px-6 py-5 border-white/20 text-white hover:bg-white/10 rounded-lg"
                data-testid="cta-login-button"
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Belonging Sessions Dashboard Link */}
          <div className="flex justify-center mt-4">
            <a 
              href="https://belongingsessions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-2 px-4 py-2 text-sm text-white/50 hover:text-amber-400 transition-colors"
              data-testid="belonging-sessions-link"
            >
              <UsersIcon size={16} color="currentColor" className="group-hover:scale-110 transition-transform" />
              <span>Return to Belonging Sessions Home</span>
              <ArrowIcon size={14} color="currentColor" direction="right" className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>
          </div>

          {/* Breathing Circle */}
          <div className="flex justify-center">
            <BreathingCircle onClick={handleBegin} />
          </div>
        </div>

        {/* Scroll Indicator - Custom Icon - Positioned below breathing circle */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce flex flex-col items-center gap-1 mt-8">
          <span className="text-white/30 text-[10px] uppercase tracking-widest">Explore</span>
          <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center bg-white/5">
            <ChevronIcon size={20} color="#FBBF24" direction="down" />
          </div>
        </div>
      </section>

      {/* First Principles - Elemental Text Experience */}
      <section className="relative py-24 px-4 sm:px-6 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-900/5 to-transparent" />
        
        <div className="max-w-4xl mx-auto relative">
          {/* EPIC descriptor */}
          <div className="text-center mb-12">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm tracking-widest uppercase mb-8">
              <span className="text-amber-400/80">Magical</span>
              <span className="text-white/20">·</span>
              <span className="text-blue-400/80">Practical</span>
              <span className="text-white/20">·</span>
              <span className="text-red-400/80">Physical</span>
              <span className="text-white/20">·</span>
              <span className="text-purple-400/80">Metaphysical</span>
              <span className="text-white/20">·</span>
              <span className="text-emerald-400/80">Spiritual</span>
              <span className="text-white/20">·</span>
              <span className="text-cyan-400/80">Logical</span>
              <span className="text-white/20">·</span>
              <span className="text-orange-400/80">Tactical</span>
              <span className="text-white/20">·</span>
              <span className="text-amber-300/80">Effective</span>
            </div>
            
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-light text-white/90 mb-2 tracking-wide"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              First Principles
            </h2>
            <p className="text-amber-400/60 text-sm tracking-[0.3em] uppercase">
              Basic Elemental Truth
            </p>
          </div>

          {/* The core wisdom */}
          <div className="space-y-8 text-center">
            <p 
              className="text-xl sm:text-2xl lg:text-3xl text-white/80 leading-relaxed font-light"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our reality is created by our{' '}
              <span className="text-amber-400">collective understanding</span>{' '}
              of each other and the world.
            </p>
            
            <div className="flex justify-center">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
            </div>
            
            <p 
              className="text-xl sm:text-2xl lg:text-3xl text-white/80 leading-relaxed font-light"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              By remembering we{' '}
              <span className="text-emerald-400">belong to each other</span>,{' '}
              we create a reality we{' '}
              <span className="text-amber-400">actually want to live in</span>.
            </p>
          </div>

          {/* Attribution */}
          <div className="mt-12 text-center">
            <span 
              className="text-lg sm:text-xl text-white/40 italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              — 
              <span className="text-amber-500/60">El</span>
              <span className="text-blue-500/60">ch</span>
              <span className="text-emerald-500/60">m</span>
              <span className="text-amber-500/60">E</span>
            </span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-semibold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The <span className="text-amber-400">Way</span> of ElchmE
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A journey from knots to bows, from conflict to connection, guided by compassion.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <FeatureCard 
              icon={ObserveIcon}
              title="Observe"
              description="See without judgment. What would a camera record?"
              color="blue"
              isBespoke={true}
              iconColor="#3B82F6"
            />
            <FeatureCard 
              icon={FeelIcon}
              title="Feel"
              description="Feelings are messengers from your needs. When needs are met, pleasant feelings arise; when unmet, painful feelings surface."
              color="red"
              isBespoke={true}
              iconColor="#EF4444"
            />
            <FeatureCard 
              icon={NeedIcon}
              title="Need"
              description="Beneath every feeling lies a universal human need. Feelings point to needs — they are intimately connected."
              color="emerald"
              isBespoke={true}
              iconColor="#10B981"
            />
            <FeatureCard 
              icon={GoldIcon}
              title="Gold"
              description="Find the resolution that honors everyone's needs."
              color="amber"
              isBespoke={true}
              iconColor="#F59E0B"
            />
          </div>
        </div>
      </section>

      {/* Feelings-Needs Connection Section */}
      <section className="relative py-20 px-4 sm:px-6 overflow-hidden">
        {/* Grant Spiral Orb Background - Large, organic, fractal */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            opacity: 0.15,
            filter: 'blur(1px)',
          }}
        >
          <div
            className="absolute w-[800px] h-[800px] rounded-full"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_nvc-workbook/artifacts/qur2gzjx_23C10E84-848E-4469-9487-21414D355AB9.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'gentlePulse 30s ease-in-out infinite, gentleRotate 60s linear infinite',
              filter: 'hue-rotate(-10deg) saturate(1.2)',
            }}
          />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-light text-white/90 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The Sacred Connection
            </h2>
            <p className="text-lg text-white/50">Feelings and Needs — Two parts of one whole</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Feelings Side */}
            <div className="relative">
              <div 
                className="bg-gradient-to-br from-rose-500/10 to-red-600/5 backdrop-blur-sm rounded-3xl p-8 border border-rose-500/20"
                style={{ boxShadow: '0 0 60px rgba(244, 63, 94, 0.1)' }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center">
                    <FeelIcon size={28} color="#F43F5E" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-rose-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Feelings
                    </h3>
                    <p className="text-rose-400/60 text-sm">The messengers within</p>
                  </div>
                </div>
                <p className="text-white/70 leading-relaxed mb-4">
                  Feelings are <span className="text-rose-300 font-medium">signals from your body</span> about whether your needs are being met. 
                  They arise naturally, without judgment — pointing you toward what matters most.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['joy', 'sadness', 'fear', 'anger', 'peace', 'longing'].map((f) => (
                    <span key={f} className="px-3 py-1 rounded-full text-xs bg-rose-500/10 text-rose-400 border border-rose-500/20">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Connection Arrow */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="flex flex-col items-center gap-2">
                <ArrowIcon size={20} color="#FBBF24" direction="right" className="animate-pulse" />
                <span className="text-amber-400/80 text-xs font-medium px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/20">
                  point to
                </span>
                <ArrowIcon size={20} color="#FBBF24" direction="left" className="animate-pulse" />
              </div>
            </div>
            
            {/* Needs Side */}
            <div className="relative">
              <div 
                className="bg-gradient-to-br from-emerald-500/10 to-green-600/5 backdrop-blur-sm rounded-3xl p-8 border border-emerald-500/20"
                style={{ boxShadow: '0 0 60px rgba(16, 185, 129, 0.1)' }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                    <NeedIcon size={28} color="#10B981" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-emerald-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Needs
                    </h3>
                    <p className="text-emerald-400/60 text-sm">The roots of all feelings</p>
                  </div>
                </div>
                <p className="text-white/70 leading-relaxed mb-4">
                  Needs are <span className="text-emerald-300 font-medium">universal human requirements</span> for wellbeing. 
                  When needs are met, we feel pleasant emotions; when unmet, painful feelings arise to guide us home.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['connection', 'safety', 'autonomy', 'meaning', 'play', 'rest'].map((n) => (
                    <span key={n} className="px-3 py-1 rounded-full text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Connection */}
          <div className="md:hidden flex justify-center my-6">
            <div className="flex items-center gap-3 px-4 py-2 bg-amber-500/10 rounded-full border border-amber-500/20">
              <ArrowIcon size={16} color="#FBBF24" direction="down" />
              <span className="text-amber-400 text-sm">Feelings point to Needs</span>
              <ArrowIcon size={16} color="#FBBF24" direction="up" />
            </div>
          </div>
          
          {/* Summary */}
          <div className="mt-12 text-center">
            <p 
              className="text-lg text-white/60 italic max-w-2xl mx-auto"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              "When we understand that every feeling is a messenger from a need, 
              we transform conflict into an opportunity for deeper connection."
            </p>
          </div>
        </div>
        
        <style>{`
          @keyframes gentlePulse {
            0%, 100% { transform: scale(1); opacity: 0.12; }
            50% { transform: scale(1.08); opacity: 0.18; }
          }
          @keyframes gentleRotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </section>

      {/* Philosophy Section */}
      <section className="relative py-20 px-4 sm:px-6 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
            <LoveAlchemyIcon size={56} color="#F59E0B" />
          </div>
          <blockquote 
            className="text-xl sm:text-2xl lg:text-3xl font-light text-white/80 leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            "The craziest thing of all is that love and compassion alchemize conflict and pain into Gold."
          </blockquote>
          <p className="text-white/40 text-sm">— The Way of ElchmE</p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <CircleOfSoulsIcon size={32} color="#3B82F6" />
              </div>
              <h3 className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Multi-Participant</h3>
              <p className="text-white/50 text-sm">Work through conflicts with multiple family members or team members together.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <SanctuaryIcon size={32} color="#10B981" />
              </div>
              <h3 className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Safe Space</h3>
              <p className="text-white/50 text-sm">A structured approach that creates safety for vulnerable conversations.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <EternalMemoryIcon size={32} color="#F59E0B" />
              </div>
              <h3 className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Save Progress</h3>
              <p className="text-white/50 text-sm">Your journeys are saved so you can return and reflect on your growth.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Wisdom Quote - Tenderness */}
      <section className="relative py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-950/20 via-transparent to-rose-950/20" />
        <div className="max-w-3xl mx-auto relative">
          <div className="relative p-8 sm:p-12 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-rose-500/10">
            {/* Decorative corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-rose-500/30 rounded-tl-2xl" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-rose-500/30 rounded-br-2xl" />
            
            <p 
              className="text-xl sm:text-2xl lg:text-3xl text-white/80 leading-relaxed font-light text-center italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              "Belonging is just{' '}
              <span className="text-rose-400 font-normal">tenderness</span>{' '}
              — when we are most in need of tenderness."
            </p>
            
            <div className="flex justify-center mt-6">
              <div className="flex items-center gap-2 text-rose-400/50 text-sm">
                <FeelIcon size={16} color="#F472B6" />
                <span style={{ fontFamily: "'Playfair Display', serif" }}>The Heart of ElchmE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gray Words Awareness Section - Sidebar style */}
      <section className="relative py-20 px-4 sm:px-6 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Gray Words Warning */}
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-zinc-600 via-zinc-500 to-zinc-600 rounded-full" />
              <div className="pl-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-zinc-700/50 border border-zinc-600/50 flex items-center justify-center">
                    <ObserveIcon size={24} color="#71717A" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-zinc-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      The Color of Non-Connection
                    </h3>
                    <p className="text-zinc-500 text-sm">Gray — Words that block communication</p>
                  </div>
                </div>
                <p className="text-zinc-400 mb-4 leading-relaxed">
                  Even when well-intended or correct, certain words tend to <span className="text-zinc-300">block genuine connection</span>. 
                  Moralizing, judging, critiquing, advising — these create resistance, not understanding.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['should', 'wrong', 'always', 'never', 'fault', 'lazy'].map((word) => (
                    <span 
                      key={word}
                      className="px-3 py-1 rounded-full text-xs bg-zinc-700/50 text-zinc-500 border border-zinc-600/30"
                    >
                      {word}
                    </span>
                  ))}
                  <span className="px-3 py-1 rounded-full text-xs text-zinc-600">...and more</span>
                </div>
                <p className="text-sm text-zinc-500 italic">
                  ElchmE helps you notice these patterns and find more connecting ways to express yourself.
                </p>
              </div>
            </div>

            {/* Green - Growth in Belonging */}
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-600 via-emerald-500 to-emerald-600 rounded-full" />
              <div className="pl-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                    <NeedIcon size={24} color="#10B981" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      What Do You Need to Grow?
                    </h3>
                    <p className="text-emerald-500/70 text-sm">Green — Your path to belonging</p>
                  </div>
                </div>
                <p className="text-zinc-400 mb-4 leading-relaxed">
                  Discover your <span className="text-emerald-400">three most essential needs</span> for growth in belonging. 
                  Understanding what you truly need creates the foundation for authentic connection.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['connection', 'understanding', 'growth', 'trust', 'purpose', 'peace'].map((need) => (
                    <span 
                      key={need}
                      className="px-3 py-1 rounded-full text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                    >
                      {need}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-emerald-400/70 italic">
                  Create your personal Belonging Song from your three key needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wisdom Quote - Ripples */}
      <section className="relative py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/20 via-transparent to-purple-950/20" />
        <div className="max-w-3xl mx-auto relative">
          <div className="relative p-8 sm:p-12 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-cyan-500/10">
            {/* Decorative corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-500/30 rounded-tl-2xl" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-purple-500/30 rounded-br-2xl" />
            
            <p 
              className="text-xl sm:text-2xl lg:text-3xl text-white/80 leading-relaxed font-light text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              "Just <span className="text-cyan-400">avoiding</span> a negative interaction{' '}
              is a <span className="text-amber-400 font-normal">massive, massive positive</span>{' '}
              — this effect <span className="text-purple-400">ripples through the universe</span>."
            </p>
            
            <div className="flex justify-center mt-6">
              <div className="flex items-center gap-2 text-purple-400/50 text-sm">
                <SparkleIcon size={16} color="#A78BFA" />
                <span style={{ fontFamily: "'Playfair Display', serif" }}>The Ripple Effect</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-semibold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to Transform?
          </h2>
          <p className="text-white/60 mb-8">
            Join the Belonging Sessions family. Begin your journey from conflict to connection.
          </p>
          <Button 
            onClick={handleBegin}
            className="px-10 py-6 text-lg font-semibold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black rounded-lg shadow-lg shadow-amber-500/20"
            data-testid="cta-begin-footer-button"
          >
            <PlayIcon size={20} color="#000000" className="mr-2" />
            Start Your ElchmE
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          {/* Main Belonging Sessions Banner */}
          <div className="mb-10">
            <a 
              href="https://belongingsessions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="relative p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-emerald-500/10 border border-amber-500/20 hover:border-amber-500/40 transition-all overflow-hidden">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/30 to-emerald-500/20 border border-amber-500/30 flex items-center justify-center">
                      <UsersIcon size={28} color="#FBBF24" />
                    </div>
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-semibold text-white group-hover:text-amber-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                        The Belonging Sessions
                      </h3>
                      <p className="text-white/50 text-sm">Your home for compassionate connection</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-amber-400/80 text-sm hidden sm:block">Visit Main Site</span>
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                      <ArrowIcon size={18} color="#FBBF24" direction="right" className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </div>
          
          {/* Sketch Hub Feature Banner */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/sketch')}
              className="group block w-full"
            >
              <div className="relative p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 via-purple-500/5 to-cyan-500/10 border border-cyan-500/20 hover:border-cyan-500/40 transition-all overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-purple-500/20 border border-cyan-500/30 flex items-center justify-center">
                      <SparkleIcon size={24} color="#06B6D4" />
                    </div>
                    <div className="text-center sm:text-left">
                      <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Sketch Hub
                      </h3>
                      <p className="text-white/50 text-sm">Epic project management & messaging canvas</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-cyan-400/80 text-sm hidden sm:block">Open Sketch Hub</span>
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                      <ArrowIcon size={18} color="#06B6D4" direction="right" className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </div>
          
          {/* Empathy SOS - Request help from verified helpers */}
          <div className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-rose-500/10 via-rose-500/5 to-amber-500/10 border border-rose-500/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold text-rose-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Need Empathy Support?
                </h3>
                <p className="text-white/50 text-sm">Request a compassionate conversation with a verified helper</p>
              </div>
              <EmpathySOS 
                userId={user?.id}
                userName={user?.name}
                triggerVariant="hero"
              />
            </div>
          </div>
          
          {/* Quick Links Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4 mb-8">
            <a 
              href="https://belongingsessions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-amber-500/30 hover:bg-amber-500/5 transition-all group"
            >
              <HomeIcon size={24} color="#9CA3AF" className="group-hover:text-amber-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-amber-400 transition-colors">Home</span>
            </a>
            <a 
              href="https://belongingsessions.com/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
            >
              <ProfileIcon size={24} color="#9CA3AF" className="group-hover:text-purple-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-purple-400 transition-colors">Dashboard</span>
            </a>
            <a 
              href="https://belongingsessions.com/lore" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-amber-500/30 hover:bg-amber-500/5 transition-all group"
            >
              <BookIcon size={24} color="#9CA3AF" className="group-hover:text-amber-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-amber-400 transition-colors">Lore Library</span>
            </a>
            <button 
              onClick={() => navigate('/elchme?mode=practice')}
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-pink-500/30 hover:bg-pink-500/5 transition-all group"
            >
              <SparkleIcon size={24} color="#9CA3AF" className="group-hover:text-pink-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-pink-400 transition-colors">Practice Cards</span>
            </button>
            <button 
              onClick={() => navigate('/elchme')}
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
            >
              <HeartIcon size={24} color="#9CA3AF" className="group-hover:text-emerald-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-emerald-400 transition-colors">Soul Score</span>
            </button>
            <a 
              href="https://belongingsessions.com/belonging-ebook" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all group"
            >
              <WisdomIcon size={24} color="#9CA3AF" className="group-hover:text-blue-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-blue-400 transition-colors">E-Book</span>
            </a>
            <a 
              href="https://playsecrethistory.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all group"
            >
              <CompassIcon size={24} color="#9CA3AF" className="group-hover:text-cyan-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-cyan-400 transition-colors text-center text-xs">Secret History Game</span>
            </a>
            <a 
              href="https://mairy.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
            >
              <MairyIcon size={24} color="#9CA3AF" className="group-hover:text-purple-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-purple-400 transition-colors">M.ai.ry</span>
            </a>
            <a 
              href="https://belongingsessions.com/events" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
            >
              <CalendarIcon size={24} color="#9CA3AF" className="group-hover:text-emerald-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-emerald-400 transition-colors">Events</span>
            </a>
            <a 
              href="https://belongingsessions.com/forum" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-rose-500/30 hover:bg-rose-500/5 transition-all group"
            >
              <UsersIcon size={24} color="#9CA3AF" className="group-hover:text-rose-400 mb-2" />
              <span className="text-white/60 text-sm group-hover:text-rose-400 transition-colors">Community</span>
            </a>
          </div>
          
          {/* ElchmE Logo and Credits */}
          <div className="text-center">
            <div className="mb-4">
              <AnimatedLogo size="sm" />
            </div>
            <p className="text-white/40 text-sm mb-2">
              <span className="text-amber-400/80">ElchmE</span> is part of <a href="https://belongingsessions.com" target="_blank" rel="noopener noreferrer" className="text-amber-400/80 hover:text-amber-400 underline underline-offset-2">The Belonging Sessions</a> with Steve Torma
            </p>
            <p className="text-white/30 text-xs tracking-widest uppercase mb-4">
              Brought to you by <a href="https://planetaryfam.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400/70 hover:text-emerald-400 underline underline-offset-2 transition-colors">planetaryFam</a> · <a href="https://defiancesprings.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400/70 hover:text-emerald-400 underline underline-offset-2 transition-colors">Defiance Springs</a>
            </p>
            <div className="flex justify-center flex-wrap gap-4 sm:gap-6 text-white/40 text-sm mb-4">
              <button onClick={() => navigate('/lore')} className="hover:text-amber-400 transition-colors">Wisdom</button>
              <button onClick={() => navigate('/belonging-ebook')} className="hover:text-amber-400 transition-colors">eBook</button>
              <button onClick={() => navigate('/sketch')} className="hover:text-amber-400 transition-colors">Sketch Hub</button>
              <a href="https://mairy.io" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">M.ai.ry</a>
              <button onClick={() => setCreditsOpen(true)} className="hover:text-amber-400 transition-colors">Credits & Gratitude</button>
              {!user && (
                <button onClick={() => setLoginOpen(true)} className="hover:text-amber-400 transition-colors">
                  Sign In
                </button>
              )}
            </div>
            <p className="text-white/20 text-xs">
              When you join, you're joining the Belonging Sessions family
            </p>
          </div>
        </div>
      </footer>

      {/* Bottom Navigation for Mobile */}
      <BottomNav active="home" />

      {/* Add padding for bottom nav */}
      <div className="h-16" />

      {/* Login Dialog */}
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />

      {/* Credits Dialog */}
      <Credits open={creditsOpen} onOpenChange={setCreditsOpen} />

      {/* Admin Feedback Button - floating */}
      <AdminFeedbackButton 
        position="bottom-right"
        userEmail={user?.email || ''}
        userName={user?.name || ''}
      />

      {/* Toaster */}
      <Toaster position="top-center" toastOptions={{ className: 'bg-[#1a1a1a] border-white/10 text-white' }} />

      {/* CSS Animations */}
      <style>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.02); filter: brightness(1.1); }
        }
        @keyframes breathe-scale {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.15); opacity: 1; }
        }
        .safe-area-pb {
          padding-bottom: env(safe-area-inset-bottom);
        }
      `}</style>
    </div>
  );
}
